/**
 * Shared library processor for library objects
 * 
 * This module provides common interfaces and utilities for processing library objects
 * and generating data packages across different platforms (C++, Roblox, etc.)
 */

import { ObjectItem, getCachedObjects } from "@/lib/stores/objectsStore";
import { getCachedTemplates } from "@/lib/stores/templatesStore";

/**
 * Processed library field with value
 */
export interface ProcessedLibraryField {
  id: string;
  name: string;
  originalName: string;
  type: string;
  value: unknown;
  isRequired: boolean;
  isTranslatable: boolean;
  isMultiple: boolean;
  comment: string;
}

/**
 * Processed library item (glossary item with values)
 */
export interface ProcessedLibraryItem {
  id: string;
  code_id?: string; // Unique code identifier for use in exports
  name: string;
  originalName: string;
  blueprintId: string | null;
  blueprintName: string | null;
  blueprintVersion: string | null;
  fields: ProcessedLibraryField[];
  tags: string[];
  identifiers: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Grouped library items by blueprint
 */
export interface LibraryItemsByBlueprint {
  blueprintId: string;
  blueprintName: string;
  blueprintVersion: string;
  items: ProcessedLibraryItem[];
}

/**
 * Process a single library object item with its field values
 */
export function processLibraryItem(item: ObjectItem, gameId: string): ProcessedLibraryItem {
  const blueprint = getCachedTemplates(gameId).find(bp => bp.id === item.blueprint_id);
  
  const fields: ProcessedLibraryField[] = [];
  
  // Process each section and field
  if (item.data && Array.isArray(item.data)) {
    item.data.forEach(section => {
      if (!section.fields || !Array.isArray(section.fields)) return;
      
      section.fields.forEach((field: any) => {
        // For library objects, the field structure is already complete from the object data
        // We just need to extract the field information and match with blueprint for types
        
        let blueprintField = null;
        let blueprintSections: any[] = [];
        
        // Extract blueprint sections from various possible structures
        if (blueprint?.data) {
          if (Array.isArray(blueprint.data)) {
            // Blueprint data is directly an array of sections
            blueprintSections = blueprint.data;
          } else if (Array.isArray((blueprint.data as any).sections)) {
            // Blueprint data has a sections property
            blueprintSections = (blueprint.data as any).sections;
          } else if (typeof blueprint.data === 'object') {
            // Blueprint data is an object with section IDs as keys
            blueprintSections = Object.entries(blueprint.data)
              .filter(([key]) => !key.startsWith('_'))
              .map(([key, value]: [string, any]) => ({
                id: key,
                ...(typeof value === 'object' ? value : {})
              }));
          }
        }
        
        // Find the blueprint field definition
        const blueprintSection = blueprintSections.find((s: any) => s.id === section.id);
        if (blueprintSection) {
          let blueprintFields: any[] = [];
          
          if (Array.isArray(blueprintSection.fields)) {
            blueprintFields = blueprintSection.fields;
          } else if (typeof blueprintSection.fields === 'object') {
            blueprintFields = Object.entries(blueprintSection.fields)
              .map(([key, value]: [string, any]) => ({
                id: key,
                ...(typeof value === 'object' ? value : {})
              }));
          }
          
          blueprintField = blueprintFields.find((f: any) => f.id === field.id);
        }
        
        // Use field label from the object data or blueprint
        const fieldName = field.label || blueprintField?.label || blueprintField?.name || field.id;
        
        // Add field with value
        fields.push({
          id: field.id,
          name: fieldName,
          originalName: fieldName,
          type: field.type || blueprintField?.type || 'text',
          value: field.value,
          isRequired: field.required || blueprintField?.isRequired || blueprintField?.required || false,
          isTranslatable: field.translation || blueprintField?.translation || false,
          isMultiple: field.multiple || blueprintField?.multiple || false,
          comment: `Field: ${fieldName} - Type: ${field.type || 'text'}${field.required ? ' - Required' : ''}`
        });
      });
    });
  }

  // Handle blueprint version - can be string or object
  let blueprintVersion = '1.0.0';
  if (blueprint?.version) {
    if (typeof blueprint.version === 'string') {
      blueprintVersion = blueprint.version;
    } else if (typeof blueprint.version === 'object' && 'version' in blueprint.version) {
      blueprintVersion = String(blueprint.version.version);
    }
  }

  return {
    id: item.id,
    code_id: item.code_id,
    name: item.name,
    originalName: item.name,
    blueprintId: item.blueprint_id || null,
    blueprintName: blueprint?.name || null,
    blueprintVersion,
    fields,
    tags: item.tags || [],
    identifiers: item.identifiers || [],
    created_at: item.created_at,
    updated_at: item.updated_at
  };
}

/**
 * Process multiple glossary items
 */
export function processLibraryItems(itemIds: string[], gameId: string): ProcessedLibraryItem[] {
  const allItems = getCachedObjects(gameId);
  
  // If no specific IDs provided, process all items
  const itemsToProcess = itemIds.length > 0 
    ? allItems.filter(item => itemIds.includes(item.id))
    : allItems;

  return itemsToProcess.map(item => processLibraryItem(item, gameId));
}

/**
 * Group library items by their blueprint
 */
export function groupItemsByBlueprint(items: ProcessedLibraryItem[]): LibraryItemsByBlueprint[] {
  const groupedMap = new Map<string, LibraryItemsByBlueprint>();
  
  items.forEach(item => {
    const blueprintKey = item.blueprintId || 'NoBlueprint';
    
    if (!groupedMap.has(blueprintKey)) {
      groupedMap.set(blueprintKey, {
        blueprintId: blueprintKey,
        blueprintName: item.blueprintName || 'UnknownBlueprint',
        blueprintVersion: item.blueprintVersion || '1.0.0',
        items: []
      });
    }
    
    groupedMap.get(blueprintKey)!.items.push(item);
  });
  
  return Array.from(groupedMap.values());
}

/**
 * Extract text from TranslatableValue objects
 * Helper to handle the new translatable value format
 */
function extractTextFromTranslatableValue(value: any): any {
  // Handle TranslatableValue objects
  if (value && typeof value === 'object' && 'text' in value) {
    return value.text;
  }
  
  // Handle arrays of TranslatableValue objects
  if (Array.isArray(value)) {
    return value.map(item => {
      if (item && typeof item === 'object' && 'text' in item) {
        return item.text;
      }
      return item;
    });
  }
  
  return value;
}

/**
 * Convert field value to JSON-serializable format
 */
export function serializeFieldValue(value: unknown, fieldType: string): unknown {
  if (value === null || value === undefined) {
    return null;
  }

  // First, extract text from TranslatableValue format if present
  const extractedValue = extractTextFromTranslatableValue(value);

  switch (fieldType) {
    case 'number':
      return Number(extractedValue) || 0;
    case 'boolean':
      return Boolean(extractedValue);
    case 'tags':
    case 'types':
      return Array.isArray(extractedValue) ? extractedValue : [];
    case 'glossaryItem':
      // If it's an array of IDs, return as is
      if (Array.isArray(extractedValue)) {
        return extractedValue;
      }
      // If it's a single ID, wrap in array
      return extractedValue ? [extractedValue] : [];
    default:
      // For text fields, handle arrays (multiple values)
      if (Array.isArray(extractedValue)) {
        return extractedValue.map(v => String(v || ''));
      }
      return String(extractedValue || '');
  }
}

/**
 * Generate JSON data for a library item
 */
export function generateItemDataJson(item: ProcessedLibraryItem): string {
  const dataObject: Record<string, unknown> = {
    object_id: item.id,
    name: item.name,
    blueprint_id: item.blueprintId,
    blueprint_version: item.blueprintVersion,
    tags: item.tags,
    identifiers: item.identifiers,
    metadata: {
      created_at: item.created_at,
      updated_at: item.updated_at
    },
    fields: {}
  };

  // Add field values
  item.fields.forEach(field => {
    (dataObject.fields as Record<string, unknown>)[field.name] = serializeFieldValue(field.value, field.type);
  });

  return JSON.stringify(dataObject, null, 2);
}

/**
 * Sanitize a string for use in filenames
 */
export function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9-_]/g, '_').replace(/_{2,}/g, '_');
}

/**
 * Sanitize a string for use in class names (remove all special characters)
 */
export function sanitizeClassName(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, '');
}

